import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react'
import { apiClient, ApiError } from '../services/apiClient'

// AuthContext에서 사용할 타입들
interface User {
  id: string
  username: string
  email: string
  role: 'admin' | 'company'
  companyId?: string
  company_id?: string
  createdAt: string
  updatedAt: string
}

interface AuthState {
  user: User | null
  token: string | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
}

// Action 타입들
type AuthAction =
  | { type: 'AUTH_START' }
  | { type: 'AUTH_SUCCESS'; payload: { user: User; token: string } }
  | { type: 'AUTH_FAILURE'; payload: string }
  | { type: 'AUTH_LOGOUT' }
  | { type: 'AUTH_CLEAR_ERROR' }

// 초기 상태
const initialState: AuthState = {
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: false,
  error: null
}

// Reducer
const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'AUTH_START':
      return {
        ...state,
        isLoading: true,
        error: null
      }
    
    case 'AUTH_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false,
        error: null
      }
    
    case 'AUTH_FAILURE':
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload
      }
    
    case 'AUTH_LOGOUT':
      return {
        ...initialState
      }
    
    case 'AUTH_CLEAR_ERROR':
      return {
        ...state,
        error: null
      }
    
    default:
      return state
  }
}

// Context 타입들
interface AuthContextType {
  state: AuthState
  login: (username: string, password: string) => Promise<void>
  logout: () => void
  clearError: () => void
  refreshToken: () => Promise<void>
}

// Context 생성
const AuthContext = createContext<AuthContextType | undefined>(undefined)

// Provider 컴포넌트
interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState)

  // 토큰에서 사용자 정보 파싱
  const parseTokenPayload = (token: string): User | null => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]))
      return {
        id: payload.userId || payload.id,
        username: payload.username,
        email: payload.email || '',
        role: payload.role,
        companyId: payload.companyId || payload.company_id,
        createdAt: payload.createdAt || payload.created_at || new Date().toISOString(),
        updatedAt: payload.updatedAt || payload.updated_at || new Date().toISOString()
      }
    } catch (error) {
      console.error('토큰 파싱 오류:', error)
      return null
    }
  }

  // 초기화 시 로컬 스토리지에서 토큰 확인
  useEffect(() => {
    const token = localStorage.getItem('token')
    if (token) {
      const user = parseTokenPayload(token)
      if (user) {
        dispatch({
          type: 'AUTH_SUCCESS',
          payload: { user, token }
        })
      } else {
        localStorage.removeItem('token')
      }
    }
  }, [])

  // 로그인
  const login = async (username: string, password: string): Promise<void> => {
    dispatch({ type: 'AUTH_START' })

    try {
      const response = await apiClient.post('/auth/login', { username, password })
      const { user, token } = response
      
      // 로컬 스토리지에 토큰 저장
      localStorage.setItem('token', token)

      dispatch({
        type: 'AUTH_SUCCESS',
        payload: { user, token }
      })
    } catch (error) {
      const errorMessage = error instanceof ApiError 
        ? error.message 
        : '로그인 중 오류가 발생했습니다.'
      
      dispatch({
        type: 'AUTH_FAILURE',
        payload: errorMessage
      })
      throw error
    }
  }

  // 로그아웃
  const logout = (): void => {
    localStorage.removeItem('token')
    dispatch({ type: 'AUTH_LOGOUT' })
  }

  // 에러 클리어
  const clearError = (): void => {
    dispatch({ type: 'AUTH_CLEAR_ERROR' })
  }

  // 토큰 갱신
  const refreshToken = async (): Promise<void> => {
    if (!state.token) return

    dispatch({ type: 'AUTH_START' })

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${state.token}`,
        },
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.message || '토큰 갱신에 실패했습니다.')
      }

      const { user, token } = data
      
      localStorage.setItem('token', token)

      dispatch({
        type: 'AUTH_SUCCESS',
        payload: { user, token }
      })
    } catch (error) {
      // 토큰 갱신 실패 시 로그아웃
      logout()
      throw error
    }
  }

  const value: AuthContextType = {
    state,
    login,
    logout,
    clearError,
    refreshToken
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

// Hook
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export default AuthContext